import React from "react";
import Reveal from "./Reveal";
import Window from "./Window";
import { VscJson } from "react-icons/vsc";
import html from "../assets/experience/html.png";
import css from "../assets/experience/css.png";
import js from "../assets/experience/javascript.png";
import reactimg from "../assets/experience/react.png";
import java from "../assets/experience/java.png";
import php from "../assets/experience/php.png";
import github from "../assets/experience/github.png";
import tailwind from "../assets/experience/tailwind.png";
import node from "../assets/experience/node.png";
import csharp from "../assets/experience/csharp.png";
import sql from "../assets/experience/sql.png";

// package.json "dependency" groups — each entry is a versioned skill.
// Items without a `src` fall back to a coloured dot.
const groups = [
  {
    key: "languages",
    items: [
      { src: java, name: "java", v: "^expert" },
      { src: csharp, name: "csharp", v: "^advanced" },
      { src: js, name: "javascript", v: "^advanced" },
      { name: "python", v: "^advanced", dot: "#4b8bbe" },
      { src: php, name: "php", v: "^proficient" },
      { name: "c", v: "^embedded", dot: "#659ad2" },
      { name: "matlab", v: "^proficient", dot: "#e16737" },
    ],
  },
  {
    key: "frontend",
    items: [
      { src: reactimg, name: "react", v: "^18.2" },
      { src: html, name: "html5", v: "^5.0" },
      { src: css, name: "css3", v: "^3.0" },
      { src: tailwind, name: "tailwindcss", v: "^3.4" },
    ],
  },
  {
    key: "backend-and-data",
    items: [
      { src: node, name: "node", v: "^20.0" },
      { src: sql, name: "sql", v: "^proficient" },
    ],
  },
  {
    key: "tools-and-workflow",
    items: [
      { src: github, name: "git", v: "^daily" },
      { name: "gitlab", v: "^ci-cd", dot: "#fc6d26" },
      { name: "playwright", v: "^e2e", dot: "#2ead33" },
      { name: "jira", v: "^daily", dot: "#2684ff" },
      { name: "agile", v: "^scrum", dot: "#4ade80" },
    ],
  },
];

const extras = [
  "c++", "mongodb", "express", "mysql", "azure",
  "linux", "ci-cd", "vercel", "rest-apis", "oop-patterns",
];

const Experience = () => {
  return (
    <section name="skills" className="relative w-full py-24 md:py-28">
      <div className="mx-auto max-w-4xl px-4">
        <Reveal className="mb-8">
          <p className="kicker">
            <span className="text-syntax-comment">02 /</span>
            <span className="text-syntax-num">skills</span>
          </p>
        </Reveal>

        <Reveal>
          <Window
            file="package.json"
            icon={<VscJson className="text-syntax-num" />}
            right={<span className="hidden text-xs text-syntax-comment sm:inline">npm ls --depth=0</span>}
          >
            <div className="overflow-x-auto p-5 text-[0.85rem] leading-7 sm:p-6 sm:text-[0.9rem]">
              <div className="whitespace-pre text-syntax-comment">{"{"}</div>

              {groups.map((group, gi) => (
                <div key={group.key} className="pl-4">
                  <div className="whitespace-pre">
                    <span className="text-syntax-prop">"{group.key}"</span>
                    <span className="text-syntax-comment">: {"{"}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-2 gap-y-1.5 py-1 pl-4">
                    {group.items.map((item, ii) => (
                      <span
                        key={item.name}
                        className="group inline-flex items-center gap-2 rounded-md border border-editor-border bg-editor-inset px-2.5 py-1 transition-colors hover:border-syntax-fn/50"
                      >
                        {item.src ? (
                          <img
                            src={item.src}
                            alt={item.name}
                            className="h-4 w-4 object-contain transition-transform group-hover:scale-110"
                          />
                        ) : (
                          <span
                            className="h-2.5 w-2.5 rounded-full transition-transform group-hover:scale-110"
                            style={{ background: item.dot }}
                          />
                        )}
                        <span className="text-syntax-str">"{item.name}"</span>
                        <span className="text-syntax-comment">:</span>
                        <span className="text-syntax-num">"{item.v}"</span>
                        {ii < group.items.length - 1 && (
                          <span className="text-syntax-comment">,</span>
                        )}
                      </span>
                    ))}
                  </div>
                  <div className="whitespace-pre text-syntax-comment">
                    {"  }"}{gi < groups.length - 1 ? "," : ""}
                  </div>
                </div>
              ))}

              <div className="whitespace-pre text-syntax-comment">{"}"}</div>

              {/* devDependencies as a trailing comment */}
              <div className="mt-4 border-t border-editor-border pt-4">
                <p className="text-syntax-comment">{"// also experienced with"}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {extras.map((s) => (
                    <span
                      key={s}
                      className="rounded-md border border-editor-border px-2.5 py-1 text-xs text-syntax-comment transition-colors hover:border-syntax-key/40 hover:text-syntax-key"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Window>
        </Reveal>
      </div>
    </section>
  );
};

export default Experience;
